/**
 * Sound.
 *
 * Every noise in this game is synthesised at runtime — no audio files, nothing
 * to download, nothing to go stale in a cache. It also means the music can
 * actually respond to play: the drone detunes as your light fails, the eat
 * blips walk up a pentatonic scale with your chain so a hot streak literally
 * sounds like a melody resolving upward.
 */

const PENTATONIC = [0, 3, 5, 7, 10, 12, 15, 17, 19, 22, 24, 27, 29]
const ROOT = 220 // A3

function noteHz(semitones: number): number {
  return ROOT * Math.pow(2, semitones / 12)
}

export class Audio {
  private ctx: AudioContext | null = null
  private master: GainNode | null = null
  private musicGain: GainNode | null = null
  private sfxGain: GainNode | null = null
  private droneOsc: OscillatorNode[] = []
  private droneFilter: BiquadFilterNode | null = null
  private noiseBuffer: AudioBuffer | null = null
  private arpStep = 0

  private _muted = false
  private _musicOn = true
  private intensity = 0

  get muted(): boolean {
    return this._muted
  }

  get musicOn(): boolean {
    return this._musicOn
  }

  /** Must be called from a user gesture; browsers will not start audio otherwise. */
  unlock(): void {
    if (this.ctx) {
      if (this.ctx.state === 'suspended') void this.ctx.resume()
      return
    }
    const Ctor = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext
    if (!Ctor) return
    const ctx = new Ctor()
    this.ctx = ctx

    const comp = ctx.createDynamicsCompressor()
    comp.threshold.value = -14
    comp.ratio.value = 6
    comp.attack.value = 0.003
    comp.release.value = 0.18

    this.master = ctx.createGain()
    this.master.gain.value = this._muted ? 0 : 0.85
    this.musicGain = ctx.createGain()
    this.musicGain.gain.value = this._musicOn ? 0.32 : 0
    this.sfxGain = ctx.createGain()
    this.sfxGain.gain.value = 0.9

    this.musicGain.connect(comp)
    this.sfxGain.connect(comp)
    comp.connect(this.master)
    this.master.connect(ctx.destination)

    // 2s of noise, reused for every percussive/breath sound.
    const len = ctx.sampleRate * 2
    const buf = ctx.createBuffer(1, len, ctx.sampleRate)
    const data = buf.getChannelData(0)
    for (let i = 0; i < len; i++) data[i] = Math.random() * 2 - 1
    this.noiseBuffer = buf

    this.startDrone()
  }

  setMuted(m: boolean): void {
    this._muted = m
    if (this.master) this.master.gain.setTargetAtTime(m ? 0 : 0.85, this.ctx!.currentTime, 0.05)
  }

  setMusic(on: boolean): void {
    this._musicOn = on
    if (this.musicGain) this.musicGain.gain.setTargetAtTime(on ? 0.32 : 0, this.ctx!.currentTime, 0.15)
  }

  /** 0..1 — how deep and how desperate the run has become. */
  setIntensity(v: number): void {
    this.intensity = Math.max(0, Math.min(1, v))
    if (this.droneFilter && this.ctx) {
      this.droneFilter.frequency.setTargetAtTime(180 + this.intensity * 900, this.ctx.currentTime, 0.4)
    }
  }

  // ── engine ─────────────────────────────────────────────────────────────

  private startDrone(): void {
    const ctx = this.ctx
    if (!ctx || !this.musicGain) return
    const filter = ctx.createBiquadFilter()
    filter.type = 'lowpass'
    filter.frequency.value = 240
    filter.Q.value = 3
    filter.connect(this.musicGain)
    this.droneFilter = filter

    for (const [detune, type, gain] of [
      [0, 'sawtooth', 0.09],
      [-7, 'sine', 0.13],
      [5, 'triangle', 0.05],
    ] as Array<[number, OscillatorType, number]>) {
      const osc = ctx.createOscillator()
      osc.type = type
      osc.frequency.value = noteHz(-24 + detune)
      const g = ctx.createGain()
      g.gain.value = gain
      const lfo = ctx.createOscillator()
      lfo.frequency.value = 0.07 + Math.random() * 0.11
      const lfoGain = ctx.createGain()
      lfoGain.gain.value = gain * 0.55
      lfo.connect(lfoGain).connect(g.gain)
      lfo.start()
      osc.connect(g).connect(filter)
      osc.start()
      this.droneOsc.push(osc, lfo)
    }
  }

  /** A sparse arpeggio that thickens as you descend. Called once per tick. */
  pulse(coil: number, alive: boolean): void {
    if (!this.ctx || !this._musicOn || !alive) return
    this.arpStep++
    const density = Math.min(6, 1 + Math.floor(coil / 2))
    if (this.arpStep % Math.max(2, 8 - density) !== 0) return
    const scale = [0, 3, 7, 10, 12, 15]
    const n = scale[(this.arpStep / 2) % scale.length | 0]
    this.blip(noteHz(n - 12), 0.5, 0.035, 'triangle', this.musicGain!)
  }

  // ── one-shots ──────────────────────────────────────────────────────────

  eat(chain: number): void {
    const step = PENTATONIC[Math.min(chain, PENTATONIC.length - 1)]
    this.blip(noteHz(step + 12), 0.16, 0.16, 'square')
    this.blip(noteHz(step + 24), 0.09, 0.06, 'sine')
  }

  ember(): void {
    this.blip(noteHz(24 + (Math.random() * 5) | 0), 0.22, 0.07, 'sine')
  }

  prism(): void {
    ;[0, 4, 7, 11, 14].forEach((n, i) => {
      setTimeout(() => this.blip(noteHz(n + 12), 0.4, 0.1, 'triangle'), i * 55)
    })
  }

  /** The signature sound: a downward tear, then a rush of released light. */
  bite(severed: number): void {
    const ctx = this.ctx
    if (!ctx || !this.sfxGain) return
    const t = ctx.currentTime
    const osc = ctx.createOscillator()
    osc.type = 'sawtooth'
    osc.frequency.setValueAtTime(noteHz(12), t)
    osc.frequency.exponentialRampToValueAtTime(noteHz(-18), t + 0.34)
    const g = ctx.createGain()
    g.gain.setValueAtTime(0.0001, t)
    g.gain.exponentialRampToValueAtTime(0.3, t + 0.015)
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.4)
    const f = ctx.createBiquadFilter()
    f.type = 'lowpass'
    f.frequency.setValueAtTime(4000, t)
    f.frequency.exponentialRampToValueAtTime(400, t + 0.35)
    osc.connect(f).connect(g).connect(this.sfxGain)
    osc.start(t)
    osc.stop(t + 0.42)

    this.noise(0.28, 0.22, 2400, 300)
    const shimmer = Math.min(8, 2 + Math.floor(severed / 3))
    for (let i = 0; i < shimmer; i++) {
      setTimeout(() => this.blip(noteHz(19 + i * 2), 0.3, 0.05, 'sine'), 140 + i * 40)
    }
  }

  coil(n: number): void {
    ;[0, 7, 12, 19].forEach((s, i) => {
      setTimeout(() => this.blip(noteHz(s + Math.min(n, 6)), 0.7, 0.12, 'triangle'), i * 80)
    })
  }

  fangs(): void {
    this.blip(noteHz(26), 0.12, 0.07, 'square')
    setTimeout(() => this.blip(noteHz(31), 0.14, 0.07, 'square'), 70)
  }

  heartbeat(): void {
    this.thud(58, 0.22, 0.24)
    setTimeout(() => this.thud(52, 0.18, 0.2), 170)
  }

  death(): void {
    const ctx = this.ctx
    if (!ctx || !this.sfxGain) return
    const t = ctx.currentTime
    const osc = ctx.createOscillator()
    osc.type = 'sawtooth'
    osc.frequency.setValueAtTime(noteHz(0), t)
    osc.frequency.exponentialRampToValueAtTime(28, t + 1.1)
    const g = ctx.createGain()
    g.gain.setValueAtTime(0.28, t)
    g.gain.exponentialRampToValueAtTime(0.0001, t + 1.3)
    osc.connect(g).connect(this.sfxGain)
    osc.start(t)
    osc.stop(t + 1.35)
    this.noise(0.9, 0.2, 1800, 120)
    this.thud(70, 0.5, 0.35)
  }

  ui(kind: 'move' | 'select' | 'back' = 'move'): void {
    const hz = kind === 'select' ? noteHz(19) : kind === 'back' ? noteHz(5) : noteHz(12)
    this.blip(hz, 0.07, 0.045, 'square')
  }

  // ── primitives ─────────────────────────────────────────────────────────

  private blip(freq: number, dur: number, gain: number, type: OscillatorType, dest?: GainNode): void {
    const ctx = this.ctx
    const out = dest ?? this.sfxGain
    if (!ctx || !out) return
    const t = ctx.currentTime
    const osc = ctx.createOscillator()
    osc.type = type
    osc.frequency.setValueAtTime(freq, t)
    const g = ctx.createGain()
    g.gain.setValueAtTime(0.0001, t)
    g.gain.exponentialRampToValueAtTime(gain, t + 0.008)
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur)
    osc.connect(g).connect(out)
    osc.start(t)
    osc.stop(t + dur + 0.02)
  }

  private thud(freq: number, dur: number, gain: number): void {
    const ctx = this.ctx
    if (!ctx || !this.sfxGain) return
    const t = ctx.currentTime
    const osc = ctx.createOscillator()
    osc.type = 'sine'
    osc.frequency.setValueAtTime(freq * 2.2, t)
    osc.frequency.exponentialRampToValueAtTime(freq, t + dur * 0.5)
    const g = ctx.createGain()
    g.gain.setValueAtTime(gain, t)
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur)
    osc.connect(g).connect(this.sfxGain)
    osc.start(t)
    osc.stop(t + dur + 0.02)
  }

  private noise(dur: number, gain: number, fromHz: number, toHz: number): void {
    const ctx = this.ctx
    if (!ctx || !this.sfxGain || !this.noiseBuffer) return
    const t = ctx.currentTime
    const src = ctx.createBufferSource()
    src.buffer = this.noiseBuffer
    const f = ctx.createBiquadFilter()
    f.type = 'bandpass'
    f.Q.value = 1.2
    f.frequency.setValueAtTime(fromHz, t)
    f.frequency.exponentialRampToValueAtTime(Math.max(40, toHz), t + dur)
    const g = ctx.createGain()
    g.gain.setValueAtTime(gain, t)
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur)
    src.connect(f).connect(g).connect(this.sfxGain)
    src.start(t)
    src.stop(t + dur + 0.02)
  }

  dispose(): void {
    for (const o of this.droneOsc) {
      try {
        o.stop()
      } catch {
        /* already stopped */
      }
    }
    this.droneOsc = []
    void this.ctx?.close()
    this.ctx = null
  }
}
